import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Wrench, Plus, Pencil, Trash2, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', salario_base: '', rol: 'mecanico',
    tipo_pago: 'sueldo', porcentaje_comision: 0, monto_por_orden: 0, base_comision: 'mano_obra' });
  const [guardando, setGuardando] = useState(false);
  const [confirmElim, setConfirmElim] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get('/staff');
      setStaff(res.data);
    } catch { toast.error('Error al cargar mecanicos'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ nombre: '', email: '', telefono: '', salario_base: '', rol: 'mecanico',
      tipo_pago: 'sueldo', porcentaje_comision: 0, monto_por_orden: 0, base_comision: 'mano_obra' });
    setDialog(true);
  };

  const abrirEditar = (m) => {
    setEditando(m);
    setForm({ nombre: m.nombre, email: m.email, telefono: m.telefono || '', salario_base: m.salario_base || '',
      rol: m.rol || 'mecanico',
      tipo_pago: m.tipo_pago || 'sueldo',
      porcentaje_comision: m.porcentaje_comision || 0,
      monto_por_orden: m.monto_por_orden || 0,
      base_comision: m.base_comision || 'mano_obra' });
    setDialog(true);
  };

  const guardar = async () => {
    if (!form.nombre || !form.email) { toast.error('Nombre y email son obligatorios'); return; }
    setGuardando(true);
    try {
      if (editando) {
        await api.put(`/staff/${editando.id}`, form);
        toast.success('Mecanico actualizado');
      } else {
        await api.post('/staff', form);
        toast.success('Mecanico creado exitosamente');
      }
      setDialog(false);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const toggleEstado = async (id, activo) => {
    try {
      await api.patch(`/staff/${id}/estado`, { activo: !activo });
      toast.success(`Mecanico ${!activo ? 'activado' : 'desactivado'}`);
      cargar();
    } catch { toast.error('Error al cambiar estado'); }
  };

  const eliminar = async (id) => {
    try {
      await api.delete(`/staff/${id}`);
      toast.success('Mecanico eliminado');
      setConfirmElim(null);
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  if (cargando) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-slate-800" /><Skeleton className="h-64 bg-slate-800" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <Wrench className="w-8 h-8 text-amber-500" /> Personal
        </h1>
        <Button onClick={abrirNuevo} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="nuevo-mecanico-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Personal
        </Button>
      </div>

      {staff.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="py-16 text-center">
            <Wrench className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No hay personal registrado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff.map(m => (
            <Card key={m.id} className={`bg-slate-900/40 border-slate-800 ${!m.activo ? 'opacity-60' : ''}`} data-testid={`staff-card-${m.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-base font-bold text-slate-200">{m.nombre}</p>
                    <p className="text-xs text-slate-500">{m.email}</p>
                    {m.telefono && <p className="text-xs text-slate-500">{m.telefono}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={m.activo ? 'bg-green-500/20 text-green-400 border-green-800' : 'bg-red-500/20 text-red-400 border-red-800'}>
                      {m.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                    <Badge className={m.rol === 'administrativo' ? 'bg-indigo-500/20 text-indigo-400 border-indigo-800' : m.rol === 'propietario' ? 'bg-amber-500/20 text-amber-400 border-amber-800' : 'bg-slate-500/20 text-slate-400 border-slate-700'}>
                      {m.rol === 'administrativo' ? 'Admin' : m.rol === 'propietario' ? 'Dueño' : 'Mecanico'}
                    </Badge>
                  </div>
                </div>
                {m.salario_base > 0 && (
                  <p className="text-xs text-slate-500 mb-3">Salario base: <span className="text-amber-500 font-mono">${Number(m.salario_base).toLocaleString('es-MX')}</span></p>
                )}
                <div className="flex items-center gap-1 border-t border-slate-800 pt-3">
                  <Button variant="ghost" size="sm" onClick={() => abrirEditar(m)}
                    className="text-blue-400 hover:text-blue-300 text-xs" data-testid={`editar-staff-${m.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => toggleEstado(m.id, m.activo)}
                    className={m.activo ? 'text-orange-400 hover:text-orange-300 text-xs' : 'text-green-400 hover:text-green-300 text-xs'}
                    data-testid={`toggle-staff-${m.id}`}>
                    {m.activo ? <><UserX className="w-3.5 h-3.5 mr-1" /> Desactivar</> : <><UserCheck className="w-3.5 h-3.5 mr-1" /> Activar</>}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmElim(m)}
                    className="text-red-400 hover:text-red-300 text-xs" data-testid={`eliminar-staff-${m.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Crear/Editar */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="staff-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">{editando ? 'Editar Personal' : 'Nuevo Personal'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nombre *</Label>
              <Input value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})}
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="staff-nombre-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Rol *</Label>
              <select value={form.rol} onChange={e => setForm({...form, rol: e.target.value})}
                className="w-full h-10 rounded-sm border bg-slate-950 border-slate-700 text-slate-200 px-3 text-sm"
                data-testid="staff-rol-select">
                <option value="mecanico">Mecanico</option>
                <option value="administrativo">Administrativo</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Email *</Label>
                <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="staff-email-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Telefono</Label>
                <Input value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="staff-telefono-input" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Salario Base (MXN)</Label>
              <Input type="number" value={form.salario_base} onChange={e => setForm({...form, salario_base: e.target.value})}
                placeholder="0.00"
                className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="staff-salario-input" /></div>

            {/* Comision */}
            <div className="border-t border-slate-800 pt-3">
              <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Forma de pago</Label>
              <div className="grid grid-cols-3 gap-1.5 mb-2">
                {[
                  { v: 'sueldo', l: 'Sueldo fijo' },
                  { v: 'comision', l: 'Solo comisión' },
                  { v: 'mixto', l: 'Sueldo + comisión' },
                ].map(op => (
                  <button key={op.v} type="button"
                    onClick={() => setForm({ ...form, tipo_pago: op.v })}
                    className={`px-2 py-2 rounded-sm text-[11px] font-bold border transition-colors ${
                      form.tipo_pago === op.v
                        ? 'bg-amber-500 border-amber-500 text-black'
                        : 'bg-slate-950 border-slate-700 text-slate-400 hover:text-slate-200'
                    }`}
                    data-testid={`staff-tipo-pago-${op.v}`}>
                    {op.l}
                  </button>
                ))}
              </div>
              {(form.tipo_pago === 'comision' || form.tipo_pago === 'mixto') && (
                <>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">% comisión (0-100)</Label>
                      <Input type="number" min="0" max="100" step="1" value={form.porcentaje_comision}
                        onChange={e => setForm({ ...form, porcentaje_comision: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                        className="bg-slate-950 border-slate-700 text-slate-200 font-mono mt-1"
                        data-testid="staff-pct-comision" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-slate-500">$ por orden</Label>
                      <Input type="number" min="0" step="1" value={form.monto_por_orden}
                        onChange={e => setForm({ ...form, monto_por_orden: Math.max(0, parseFloat(e.target.value) || 0) })}
                        className="bg-slate-950 border-slate-700 text-slate-200 font-mono mt-1"
                        data-testid="staff-monto-orden" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Comisión se calcula sobre</Label>
                    <div className="grid grid-cols-2 gap-1.5 mt-1">
                      <button type="button"
                        onClick={() => setForm({ ...form, base_comision: 'mano_obra' })}
                        className={`px-2 py-2 rounded-sm text-[11px] font-bold border ${form.base_comision === 'mano_obra' ? 'bg-amber-500 border-amber-500 text-black' : 'bg-slate-950 border-slate-700 text-slate-400'}`}
                        data-testid="staff-base-mano-obra">
                        Solo mano de obra
                      </button>
                      <button type="button"
                        onClick={() => setForm({ ...form, base_comision: 'total' })}
                        className={`px-2 py-2 rounded-sm text-[11px] font-bold border ${form.base_comision === 'total' ? 'bg-amber-500 border-amber-500 text-black' : 'bg-slate-950 border-slate-700 text-slate-400'}`}
                        data-testid="staff-base-total">
                        Total de orden (con refacciones)
                      </button>
                    </div>
                  </div>
                </>
              )}
              <p className="text-[10px] text-slate-500 mt-2">
                {form.tipo_pago === 'sueldo' && 'El mecánico recibe solo su salario base.'}
                {form.tipo_pago === 'comision' && 'El mecánico recibe % + monto fijo por cada orden pagada asignada.'}
                {form.tipo_pago === 'mixto' && 'El mecánico recibe salario base MÁS comisión.'}
              </p>
            </div>
            {!editando && (
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Contrasena de Acceso</Label>
                <Input type="password" value={form.password || ''} onChange={e => setForm({...form, password: e.target.value})}
                  placeholder="Dejar vacio si no tendra acceso al sistema"
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="staff-password-input" />
                <p className="text-[10px] text-slate-500 mt-1">Si asigna contrasena, el personal podra iniciar sesion con su email y contrasena.</p>
              </div>
            )}
          </div>
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={guardar} disabled={guardando} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="guardar-staff-btn">
              {guardando ? 'Guardando...' : editando ? 'Actualizar' : 'Crear Personal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={!!confirmElim} onOpenChange={() => setConfirmElim(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-sm" data-testid="confirm-delete-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-lg text-slate-100">Confirmar Eliminacion</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">Esta seguro de eliminar a <span className="text-slate-200 font-medium">{confirmElim?.nombre}</span>? Esta accion no se puede deshacer.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmElim(null)} className="text-slate-400">Cancelar</Button>
            <Button onClick={() => eliminar(confirmElim?.id)} className="bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="confirmar-eliminar-btn">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
